import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import Sidebar from "../Sidebar/Sidebar";
import { Redirect } from "react-router-dom";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ClearAll as ClearAllIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { toast } from "react-toastify";

toast.configure();

const filterOptions = createFilterOptions({
  limit: 15,
  trim: true,
});

const compactInputSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const compactAutoSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
    pr: "32px !important",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px !important",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const gridEditAutoSx = {
  width: "100%",
  minWidth: 340,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontSize: 13,
    pr: "32px !important",
    bgcolor: "#fff",
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 10px !important",
    fontSize: 13,
  },
};

function getApiBase() {
  return process.env.REACT_APP_API_URL || "";
}

function buildApiUrl(apiBase, path, params) {
  const url = `${apiBase}${path}`;
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

function getAuthHeaders(userToken, withJson = false) {
  return {
    ...(withJson ? { "Content-Type": "application/json" } : {}),
    sptoken: userToken,
  };
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
}

async function requestJson(url, options) {
  const response = await fetch(url, options);
  const data = await parseResponse(response);

  if (!response.ok) {
    throw new Error(
      typeof data === "string" ? data : data?.message || "Request failed",
    );
  }

  return data;
}

function formatDateForInput(value) {
  if (!value) return dayjs().format("YYYY-MM-DD");
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
}

function normalizeText(value) {
  return String(value || "").trim();
}

function getPartyLabel(party) {
  return party?.partydetails2 || party?.partydetails || "";
}

function rowKeyFromReqDtl(warrantyreqdtlid) {
  return `REQDTL_${warrantyreqdtlid}`;
}

function finishCellEdit(api, id, field, options = {}) {
  if (typeof api?.stopCellEditMode === "function") {
    api.stopCellEditMode({ id, field, ...options });
    return;
  }

  if (typeof api?.commitCellChange === "function") {
    api.commitCellChange({ id, field });
  }

  if (typeof api?.setCellMode === "function") {
    api.setCellMode(id, field, "view");
  }
}

function makeGridRowFromTreeItem(item, idx) {
  return {
    id: `${rowKeyFromReqDtl(item.warrantyreqdtlid)}_${Date.now()}_${idx}`,
    srl: idx,
    warrantyreqid: item.warrantyreqid || null,
    warrantyreqdtlid: item.warrantyreqdtlid || null,
    warrantyclaimid: item.warrantyclaimid || null,
    warrantyclaimdtlid: item.warrantyclaimdtlid || null,
    reqdocno: item.reqdocno || "",
    reqsrl: item.reqsrl || null,
    claimdocno: item.claimdocno || "",
    claimsrl: item.claimsrl || null,
    ticketno: item.ticketno || "",
    againstitemid: item.againstitemid || null,
    againstitemcode: item.againstitemcode || "",
    againstitemname: item.againstitemname || "",
    againstitemsrlno: item.againstitemsrlno || "",
    issueditemid: item.againstitemid || null,
    issueditemcode: item.againstitemcode || "",
    issueditemname: item.againstitemname || "",
    issuedsrlno: "",
    claimedflag: item.claimedflag === true,
    claimstatustext: item.claimedflag ? "CLAIMED" : "NOT CLAIMED",
    remarks: item.remarks || "",
  };
}

function PartEditAutocomplete(props) {
  const { id, field, value, api, allparts, loading, onSelectItem } = props;

  const selected =
    allparts.find(
      (part) =>
        String(part.itemcode || "").toUpperCase() ===
          String(value || "").toUpperCase() || part.itemid === value,
    ) || null;

  return (
    <Autocomplete
      size="small"
      options={allparts}
      value={selected}
      loading={loading}
      autoHighlight
      sx={gridEditAutoSx}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 420,
          },
        },
        popper: {
          sx: {
            minWidth: 420,
          },
        },
      }}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        option ? `${option.itemcode || ""} - ${option.itemname || ""}` : ""
      }
      isOptionEqualToValue={(option, currentValue) =>
        option?.itemid === currentValue?.itemid
      }
      onChange={async (event, newValue) => {
        const item = newValue || null;
        const nextCode = item?.itemcode || "";

        if (typeof onSelectItem === "function") {
          onSelectItem(id, item);
        }

        if (typeof api?.setEditCellValue === "function") {
          await api.setEditCellValue({
            id,
            field,
            value: nextCode,
          });
        }

        finishCellEdit(api, id, field, { ignoreModifications: true });
      }}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.itemid || option.itemcode}>
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15 }}
            >
              {option.itemcode} - {option.itemname}
            </Typography>
            <Typography
              sx={{ fontSize: 10.25, color: "text.secondary", lineHeight: 1.1 }}
            >
              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
              {option.rackname || "N/A"}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} autoFocus size="small" sx={gridEditAutoSx} />
      )}
    />
  );
}

function TreeDocCard({
  doc,
  expanded,
  onToggleExpand,
  onAddWholeDoc,
  onAddSingleItem,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        mb: 1,
        "& .MuiCardContent-root:last-child": { pb: 1.25 },
      }}
    >
      <CardContent sx={{ p: 1.25 }}>
        <Stack spacing={0.75}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <IconButton size="small" onClick={() => onToggleExpand(doc.warrantyreqid)}>
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {doc.docno}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2, wordBreak: "break-word" }}
                >
                  {doc.partydtls || "-"}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                label={dayjs(doc.docdate).isValid() ? dayjs(doc.docdate).format("DD-MM-YYYY") : ""}
              />
              <Chip size="small" color="primary" variant="outlined" label={`${doc.itemcount || 0} items`} />
              <Button
                size="small"
                variant="contained"
                onClick={() => onAddWholeDoc(doc)}
                sx={{ minWidth: 0, px: 1.2, py: 0.4, fontSize: 12 }}
              >
                Add Doc
              </Button>
            </Stack>
          </Stack>

          <Collapse in={expanded}>
            <Stack spacing={0.5} sx={{ pt: 0.25 }}>
              {(doc.children || []).map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 0.75, borderRadius: 1.25, bgcolor: "background.default" }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack spacing={0.2} sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
                        <b>{item.againstitemcode || "-"}</b>
                        {" - "}
                        {item.againstitemname || "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, wordBreak: "break-word" }}
                      >
                        Req Srl: {item.reqsrl || "-"} | Ticket: {item.ticketno || "-"} | Against Srl: {item.againstitemsrlno || "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        size="small"
                        label={item.claimstatustext || "NOT CLAIMED"}
                        color={item.claimedflag ? "success" : "default"}
                        variant={item.claimedflag ? "filled" : "outlined"}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onAddSingleItem(item)}
                        sx={{ minWidth: 0, px: 1, py: 0.35, fontSize: 12 }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function WarrantyIssue() {
  const apiBase = getApiBase();
  const userToken = LocalstorageHelper.GetUserToken();

  const [issueDate, setIssueDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [partyList, setPartyList] = useState([]);
  const [partyValue, setPartyValue] = useState(null);
  const [issueRemarks, setIssueRemarks] = useState("");
  const [searchText, setSearchText] = useState("");
  const [treeData, setTreeData] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState({});
  const [rows, setRows] = useState([]);
  const [loadingParty, setLoadingParty] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allparts, setAllParts] = useState([]);

  const apiRefGuard = useRef(false);

  useEffect(() => {
    if (!userToken) return undefined;

    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);

    return () => clearTimeout(timeout);
  }, [userToken]);

  const totalTreeItems = useMemo(() => {
    return treeData.reduce((acc, doc) => acc + (doc.children?.length || 0), 0);
  }, [treeData]);

  const claimedCount = useMemo(() => {
    return treeData.reduce((acc, doc) => {
      return acc + (doc.children || []).filter((item) => item.claimedflag).length;
    }, 0);
  }, [treeData]);

  const notClaimedCount = useMemo(() => {
    return totalTreeItems - claimedCount;
  }, [totalTreeItems, claimedCount]);

  const renumberRows = useCallback((list) => {
    return list.map((row, idx) => ({
      ...row,
      srl: idx + 1,
    }));
  }, []);

  const loadParties = useCallback(async () => {
    if (!userToken) {
      return;
    }

    setLoadingParty(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/issue/partydata"),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      const list = Array.isArray(data) ? data : [];
      setPartyList(list);

      setPartyValue((prev) => {
        if (!prev?.subledgerid) {
          return null;
        }

        return (
          list.find(
            (party) =>
              Number(party.subledgerid) === Number(prev.subledgerid),
          ) || null
        );
      });
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load party list");
    } finally {
      setLoadingParty(false);
    }
  }, [apiBase, userToken]);

  useEffect(() => {
    loadParties();
  }, [loadParties]);

  const loadAllParts = useCallback(async () => {
    if (!userToken) {
      return;
    }

    setLoadingParts(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/claim/allparts"),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      setAllParts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load parts");
    } finally {
      setLoadingParts(false);
    }
  }, [apiBase, userToken]);

  useEffect(() => {
    loadAllParts();
  }, [loadAllParts]);

  const handleToggleExpand = useCallback((warrantyreqid) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [warrantyreqid]: !prev[warrantyreqid],
    }));
  }, []);

  const isReqDtlAlreadyAdded = useCallback(
    (warrantyreqdtlid) => {
      return rows.some(
        (row) => Number(row.warrantyreqdtlid) === Number(warrantyreqdtlid),
      );
    },
    [rows],
  );

  const addItemsToGrid = useCallback(
    (items) => {
      if (!items?.length) {
        return;
      }

      const newItems = [];
      let skipped = 0;

      items.forEach((item, idx) => {
        if (isReqDtlAlreadyAdded(item.warrantyreqdtlid)) {
          skipped += 1;
          return;
        }

        newItems.push(
          makeGridRowFromTreeItem(
            item,
            rows.length + newItems.length + idx + 1,
          ),
        );
      });

      if (!newItems.length) {
        toast.info("Selected items are already added");
        return;
      }

      setRows((prev) => renumberRows([...prev, ...newItems]));

      if (skipped > 0) {
        toast.info(
          `${newItems.length} item(s) added, ${skipped} already existed`,
        );
      } else {
        toast.success(`${newItems.length} item(s) added`);
      }
    },
    [isReqDtlAlreadyAdded, renumberRows, rows.length],
  );

  const handleAddWholeDoc = useCallback(
    (doc) => {
      const children = doc.children || [];

      if (!children.length) {
        toast.info("No items found in this document");
        return;
      }

      const ok = window.confirm(
        `Add all ${children.length} item(s) from document ${doc.docno}?`,
      );

      if (!ok) {
        return;
      }

      addItemsToGrid(children);
    },
    [addItemsToGrid],
  );

  const handleAddSingleItem = useCallback(
    (item) => {
      addItemsToGrid([item]);
    },
    [addItemsToGrid],
  );

  const handleFetch = useCallback(async () => {
    if (!userToken) {
      return;
    }

    if (!partyValue?.subledgerid) {
      toast.error("Select party first");
      return;
    }

    setLoadingTree(true);

    try {
      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/issue/search", {
          partyid: partyValue.subledgerid,
          search: searchText || "",
        }),
        {
          method: "GET",
          headers: getAuthHeaders(userToken),
        },
      );

      if (data?.type === "error") {
        toast.error(data.message || "Failed to fetch issue data");
        setTreeData([]);
        setExpandedDocs({});
        return;
      }

      const list = Array.isArray(data?.treeData) ? data.treeData : [];
      setTreeData(list);

      const nextExpanded = {};
      list.forEach((doc) => {
        nextExpanded[doc.warrantyreqid] = true;
      });
      setExpandedDocs(nextExpanded);

      if (!list.length) {
        toast.info("No request rows found");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to fetch issue data");
      setTreeData([]);
      setExpandedDocs({});
    } finally {
      setLoadingTree(false);
    }
  }, [apiBase, partyValue, searchText, userToken]);

  const fetchItemByCode = useCallback(
    async (code) => {
      if (!userToken) {
        return null;
      }

      const cleanCode = normalizeText(code).toUpperCase();

      if (!cleanCode) {
        return null;
      }

      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/claim/allparts/detailfetch"),
        {
          method: "POST",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify({
            code: cleanCode,
          }),
        },
      );

      return data?.itemid ? data : null;
    },
    [apiBase, userToken],
  );

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const oldCode = normalizeText(oldRow.issueditemcode).toUpperCase();
      const newCode = normalizeText(newRow.issueditemcode).toUpperCase();

      const updatedRow = {
        ...newRow,
        issueditemcode: newCode,
        issuedsrlno: normalizeText(newRow.issuedsrlno),
        remarks: normalizeText(newRow.remarks),
      };

      if (!newCode) {
        updatedRow.issueditemid = null;
        updatedRow.issueditemname = "";
      } else if (!updatedRow.issueditemid || newCode !== oldCode) {
        const item = await fetchItemByCode(newCode);

        if (!item) {
          throw new Error("Invalid issued item code");
        }

        updatedRow.issueditemid = item.itemid;
        updatedRow.issueditemcode = item.itemcode;
        updatedRow.issueditemname = item.itemname;
      }

      setRows((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)),
      );

      return updatedRow;
    },
    [fetchItemByCode],
  );

  const handleProcessRowUpdateError = useCallback((error) => {
    console.error(error);
    toast.error(error?.message || "Row update failed");
  }, []);

  const handleDeleteRow = useCallback(
    (id) => {
      setRows((prev) => renumberRows(prev.filter((row) => row.id !== id)));
    },
    [renumberRows],
  );

  const handleIssuedItemSelect = useCallback((rowId, item) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              issueditemid: item?.itemid || null,
              issueditemcode: item?.itemcode || "",
              issueditemname: item?.itemname || "",
            }
          : row,
      ),
    );
  }, []);

  const validateBeforeSave = useCallback(() => {
    if (!partyValue?.subledgerid) {
      toast.error("Party is required");
      return false;
    }

    if (!issueDate) {
      toast.error("Issue date is required");
      return false;
    }

    if (!rows.length) {
      toast.error("Add at least one row");
      return false;
    }

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];

      if (!row.issueditemid && !normalizeText(row.issueditemcode)) {
        toast.error(`Issued item missing at row ${i + 1}`);
        return false;
      }

      if (!normalizeText(row.issuedsrlno)) {
        toast.error(`Issued serial no missing at row ${i + 1}`);
        return false;
      }
    }

    return true;
  }, [issueDate, partyValue, rows]);

  const handleSave = useCallback(async () => {
    if (!userToken || apiRefGuard.current) {
      return;
    }

    if (!validateBeforeSave()) {
      return;
    }

    setSaving(true);
    apiRefGuard.current = true;

    try {
      const payload = {
        issuedate: issueDate,
        subledgerid: partyValue?.subledgerid || null,
        issueremarks: issueRemarks || "",
        rows: rows.map((row, idx) => ({
          srl: idx + 1,
          warrantyreqid: row.warrantyreqid || null,
          warrantyreqdtlid: row.warrantyreqdtlid || null,
          warrantyclaimid: row.warrantyclaimid || null,
          warrantyclaimdtlid: row.warrantyclaimdtlid || null,
          reqdocno: row.reqdocno || null,
          reqsrl: row.reqsrl || null,
          claimdocno: row.claimdocno || null,
          claimsrl: row.claimsrl || null,
          ticketno: row.ticketno || "",
          againstitemid: row.againstitemid || null,
          againstitemcode: row.againstitemcode || "",
          againstitemname: row.againstitemname || "",
          againstitemsrlno: row.againstitemsrlno || "",
          issueditemid: row.issueditemid || null,
          issueditemcode: normalizeText(row.issueditemcode),
          issueditemname: row.issueditemname || "",
          issuedsrlno: normalizeText(row.issuedsrlno),
          claimedflag: row.claimedflag === true,
          remarks: normalizeText(row.remarks),
        })),
      };

      const data = await requestJson(
        buildApiUrl(apiBase, "/warranty/issue/insert"),
        {
          method: "POST",
          headers: getAuthHeaders(userToken, true),
          body: JSON.stringify(payload),
        },
      );

      if (data?.type === "success") {
        toast.success(data.message || "Saved successfully");
        setRows([]);
        setIssueRemarks("");
        setTreeData([]);
        setExpandedDocs({});
        setSearchText("");
      } else {
        toast.error(data?.message || "Save failed");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Save failed");
    } finally {
      setSaving(false);
      apiRefGuard.current = false;
    }
  }, [
    apiBase,
    issueDate,
    issueRemarks,
    partyValue,
    rows,
    userToken,
    validateBeforeSave,
  ]);

  const handleClearAll = useCallback(() => {
    if (!rows.length) {
      return;
    }

    const ok = window.confirm("Clear selected rows?");
    if (!ok) {
      return;
    }

    setRows([]);
  }, [rows.length]);

  const handlePartyChange = useCallback(
    (event, newValue) => {
      const nextPartyId = Number(newValue?.subledgerid || 0);
      const currentPartyId = Number(partyValue?.subledgerid || 0);

      if (
        currentPartyId &&
        nextPartyId !== currentPartyId &&
        (rows.length > 0 || treeData.length > 0)
      ) {
        const ok = window.confirm(
          "Changing party will clear fetched request rows and current issue rows. Continue?",
        );

        if (!ok) {
          return;
        }

        setRows([]);
        setTreeData([]);
        setExpandedDocs({});
        setSearchText("");
      }

      setPartyValue(newValue || null);
    },
    [partyValue, rows.length, treeData.length],
  );

  const columns = useMemo(
    () => [
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        sortable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "reqdocno",
        headerName: "Req Doc",
        width: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "reqsrl",
        headerName: "Req Srl",
        width: 70,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimdocno",
        headerName: "Claim Doc",
        width: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "claimstatustext",
        headerName: "Claim Status",
        width: 120,
        sortable: false,
        headerClassName: "super-app-theme--header",
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value || "NOT CLAIMED"}
            color={params.row.claimedflag ? "success" : "default"}
            variant={params.row.claimedflag ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "ticketno",
        headerName: "Ticket",
        width: 110,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstitemcode",
        headerName: "Against Item",
        width: 320,
        headerClassName: "super-app-theme--header",
        renderCell: (params) =>
          `${params.row.againstitemcode || ""}${
            params.row.againstitemname ? ` - ${params.row.againstitemname}` : ""
          }`,
      },
      {
        field: "againstitemsrlno",
        headerName: "Against Srl",
        width: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "issueditemcode",
        headerName: "Issued Item",
        width: 320,
        editable: true,
        headerClassName: "super-app-theme--header",
        renderCell: (params) =>
          `${params.row.issueditemcode || ""}${
            params.row.issueditemname ? ` - ${params.row.issueditemname}` : ""
          }`,
        renderEditCell: (params) => (
          <PartEditAutocomplete
            {...params}
            allparts={allparts}
            loading={loadingParts}
            onSelectItem={handleIssuedItemSelect}
          />
        ),
      },
      {
        field: "issuedsrlno",
        headerName: "Issued Srl No",
        width: 130,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "remarks",
        headerName: "Remarks",
        width: 180,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 46,
        headerClassName: "super-app-theme--header",
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={() => handleDeleteRow(params.id)}
            color="inherit"
          />,
        ],
      },
    ],
    [allparts, handleDeleteRow, handleIssuedItemSelect, loadingParts],
  );

  if (!userToken) {
    return <Redirect exact to="/sparepartsLogin" />;
  }

  return (
    <Box>
      <Sidebar
        Title={"WARRANTY ISSUE"}
        expandedPanel={"panel2"}
        children={
          <Box
            sx={{
              width: "100%",
              "& .super-app-theme--header": {
                backgroundColor: "#ebf5ff",
                color: "#0d3557",
                fontWeight: 800,
                fontSize: 11.25,
              },
            }}
          >
            <Backdrop
              open={saving}
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 999,
                backdropFilter: "blur(1.5px)",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={24} />
              <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                Saving issue document...
              </Typography>
            </Backdrop>

            <Stack spacing={1.25}>
              <Card elevation={1}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={0.75}
                      alignItems={{ xs: "stretch", md: "center" }}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <TextField
                        type="date"
                        label="Issue Date"
                        size="small"
                        value={formatDateForInput(issueDate)}
                        onChange={(event) => setIssueDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 150, ...compactInputSx }}
                      />

                      <Autocomplete
                        size="small"
                        options={partyList}
                        loading={loadingParty}
                        value={partyValue}
                        filterOptions={filterOptions}
                        onChange={handlePartyChange}
                        getOptionLabel={(option) => getPartyLabel(option)}
                        isOptionEqualToValue={(option, value) =>
                          Number(option?.subledgerid) ===
                          Number(value?.subledgerid)
                        }
                        sx={{ minWidth: 320, flex: 1, ...compactAutoSx }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Party"
                            placeholder="Select party"
                            size="small"
                          />
                        )}
                      />

                      <TextField
                        size="small"
                        label="Search"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder="Doc / ticket / item / serial"
                        sx={{ minWidth: 240, ...compactInputSx }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleFetch();
                          }
                        }}
                      />

                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          loadingTree ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        onClick={handleFetch}
                        disabled={loadingTree}
                        sx={{
                          borderRadius: 1.2,
                          minWidth: 88,
                          height: 31,
                          fontSize: 12,
                        }}
                      >
                        Fetch
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadParties}
                        disabled={loadingParty}
                        sx={{
                          borderRadius: 1.2,
                          minWidth: 120,
                          height: 31,
                          fontSize: 12,
                        }}
                      >
                        Reload Party
                      </Button>
                    </Stack>

                    <TextField
                      size="small"
                      label="Issue Remarks"
                      value={issueRemarks}
                      onChange={(event) => setIssueRemarks(event.target.value)}
                      fullWidth
                      sx={compactInputSx}
                    />

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`Docs: ${treeData.length}`} variant="outlined" />
                      <Chip size="small" label={`Items: ${totalTreeItems}`} variant="outlined" />
                      <Chip
                        size="small"
                        label={`Claimed: ${claimedCount}`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`Not Claimed: ${notClaimedCount}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`Grid Rows: ${rows.length}`}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={1.25}
                alignItems="stretch"
              >
                <Card
                  elevation={1}
                  sx={{
                    width: { xs: "100%", lg: 430 },
                    minWidth: { lg: 430 },
                  }}
                >
                  <CardContent
                    sx={{
                      p: "7px 8px !important",
                      maxHeight: "72vh",
                      overflow: "auto",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                          Request Items
                        </Typography>
                        {loadingTree && <CircularProgress size={18} />}
                      </Stack>

                      {!partyValue?.subledgerid ? (
                        <Alert severity="info" sx={{ py: 0 }}>
                          Select party and click Fetch.
                        </Alert>
                      ) : !loadingTree && !treeData.length ? (
                        <Alert severity="warning" sx={{ py: 0 }}>
                          No rows found.
                        </Alert>
                      ) : null}

                      <Box>
                        {treeData.map((doc) => (
                          <TreeDocCard
                            key={doc.id}
                            doc={doc}
                            expanded={!!expandedDocs[doc.warrantyreqid]}
                            onToggleExpand={handleToggleExpand}
                            onAddWholeDoc={handleAddWholeDoc}
                            onAddSingleItem={handleAddSingleItem}
                          />
                        ))}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <Card elevation={1} sx={{ flex: 1, minWidth: 0 }}>
                  <CardContent sx={{ p: "7px 8px !important" }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                          Issue Grid
                        </Typography>

                        <Stack direction="row" spacing={0.75}>
                          <Tooltip title="Clear all rows">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<ClearAllIcon />}
                                onClick={handleClearAll}
                                disabled={!rows.length}
                                sx={{
                                  borderRadius: 1.2,
                                  minWidth: 80,
                                  height: 31,
                                  fontSize: 12,
                                }}
                              >
                                Clear
                              </Button>
                            </span>
                          </Tooltip>

                          <Tooltip title="Save issue">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={
                                  saving ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <SaveIcon />
                                  )
                                }
                                onClick={handleSave}
                                disabled={saving || !rows.length}
                                sx={{
                                  borderRadius: 1.2,
                                  minWidth: 80,
                                  height: 31,
                                  fontSize: 12,
                                }}
                              >
                                Save
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      <Divider />

                      <Box
                        sx={{
                          height: "66vh",
                          width: "100%",
                          "& .MuiDataGrid-cell": {
                            fontSize: 11.25,
                            py: 0,
                          },
                          "& .MuiDataGrid-footerContainer": {
                            minHeight: 36,
                          },
                        }}
                      >
                        <DataGrid
                          rows={rows}
                          columns={columns}
                          getRowId={(row) => row.id}
                          density="compact"
                          rowHeight={27}
                          headerHeight={29}
                          disableSelectionOnClick
                          editMode="cell"
                          experimentalFeatures={{ newEditingApi: true }}
                          processRowUpdate={processRowUpdate}
                          onProcessRowUpdateError={handleProcessRowUpdateError}
                          rowsPerPageOptions={[10, 20, 30, 50]}
                          initialState={{
                            sorting: {
                              sortModel: [{ field: "srl", sort: "asc" }],
                            },
                            pagination: {
                              pageSize: 20,
                            },
                          }}
                          sx={{
                            border: "1px solid #dbe7f3",
                            "& .MuiDataGrid-cell:focus": {
                              outline: "1px solid #1976d2",
                            },
                            "& .MuiDataGrid-cell:focus-within": {
                              outline: "1px solid #1976d2",
                            },
                          }}
                        />
                      </Box>

                      <Alert severity="info" sx={{ py: 0 }}>
                        Issued item defaults from against item. You can change issued code. Issued serial no is mandatory.
                      </Alert>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </Box>
        }
      />
    </Box>
  );
}
